import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { USER_STATE } from "consts";
import { assertUserPermissions } from "utils";
//
import {
    withStyles,
    makeStyles,
    useTheme,
    useMediaQuery,
    Badge,
    Box,
    Divider,
    Drawer,
    Grid,
    IconButton,
    List,
    ListItem,
    Tooltip,
    Typography,
} from "@material-ui/core";
import {
    Home as HomeIcon,
    TableChart as TableChartIcon,
    Lock as LockIcon,
    NotificationsPaused as NotificationsPausedIcon,
    AssignmentInd as AssignmentIndIcon,
    Assignment as AssignmentIcon,
    PowerSettingsNew as PowerSettingsNewIcon,
    ExitToApp as ExitToAppIcon,
    PersonAdd as PersonAddIcon,
    ChevronLeft as ChevronLeftIcon,
    Room as RoomIcon,
    Settings as SettingsIcon,
    Event as EventIcon,
    Publish as PublishIcon,
    LiveHelp as LiveHelpIcon,
    NewReleasesOutlined as NewReleasesOutlinedIcon,
} from "@material-ui/icons";
//
import { NoticeContext } from "App";
import TTMSLogo from "containers/components/logo/TTMSLogo";
import ChangelogDialog from "containers/components/changelog/ChangelogDialog";
import MenuButton from "containers/components/menuItem/MenuButton";
import MenuLink from "containers/components/menuItem/MenuLink";
import LoggedAs from "containers/components/misc/LoggedAs";
import Version from "containers/components/misc/Version";
import ThemeSwitchButton from "containers/components/themeSwitchButton/ThemeSwitchButton";

const drawerWidth = "240px";

const StyledBadge = withStyles((theme) => ({
    badge: {
        right: 8,
        top: 8,
        padding: "0 4px",
    },
}))(Badge);

const useStyles = makeStyles((theme) => ({
    buttonBorder: {
        border: "1px solid",
        borderColor: theme.palette.text.secondary,
        borderRadius: 12,
        padding: theme.spacing(1),
        width: 40,
        height: 40,
    },
    drawer: {
        [theme.breakpoints.up("sm")]: {
            width: drawerWidth,
            flexShrink: 0,
            boxShadow: "4px 0px 6px rgba(0, 0, 0, 0.16)",
        },
    },
    menuButton: {
        marginRight: theme.spacing(2),
        [theme.breakpoints.up("sm")]: {
            display: "none",
        },
    },
    drawerToggleContainer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginLeft: theme.spacing(2),

        [theme.breakpoints.up("sm")]: {
            display: "none",
        },
    },
    toolbar: theme.mixins.toolbar,
    drawerPaper: {
        width: drawerWidth,
    },
    content: {
        flexGrow: 1,
        padding: theme.spacing(3),
    },
    versionItem: {
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        alignItems: "flex-start",
        padding: "4px",
    },
}));

function SideMenu(props) {
    const { user, mobileOpen } = props;
    const history = useHistory();
    const classes = useStyles();
    const theme = useTheme();
    const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

    const onLogout = props.onLogoutHandler;
    const onLogin = props.onLoginHandler;
    const handleDrawerToggle = props.handleDrawerToggle;
    const { overtimes, accessRequests } = useContext(NoticeContext);
    const [openDialog] = useState(false);

    const gotoSettings = () => {
        handleDrawerToggle();
        history.push("/settings");
    };

    const goToChangelog = () => {
        handleDrawerToggle();
        history.push("/changelog");
    };

    return (
        <nav className={classes.drawer}>
            <Drawer
                variant={isSmUp ? "permanent" : "temporary"}
                anchor={theme.direction === "rtl" ? "right" : "left"}
                open={mobileOpen}
                onClose={handleDrawerToggle}
                classes={{
                    paper: classes.drawerPaper,
                }}
                ModalProps={{
                    keepMounted: true,
                }}
            >
                <Box className={classes.drawerToggleContainer}>
                    <IconButton onClick={handleDrawerToggle}>
                        <ChevronLeftIcon />
                    </IconButton>
                </Box>

                <Divider />

                <div className="brand-container">
                    <TTMSLogo className="brand-logo" />
                    <Typography component={isSmUp ? "h1" : "span"} className="brand-title">
                        TimeSheets
                    </Typography>
                </div>

                <Divider />

                <List
                    id="menu"
                    component="menu"
                    className="main-menu"
                    aria-labelledby="nested-list-subheader"
                >
                    <ListItem>
                        <Grid container spacing={1}>
                            <Grid item>
                                <ThemeSwitchButton />
                            </Grid>
                            {props.user && (
                                <Grid item>
                                    <Tooltip title="User settings">
                                        <IconButton
                                            onClick={gotoSettings}
                                            color="primary"
                                            className={classes.buttonBorder}>
                                            <SettingsIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            )}
                            {props.user && (
                                <Grid item>
                                    <Tooltip title="Release notes">
                                        <IconButton
                                            onClick={goToChangelog}
                                            color="primary"
                                            className={classes.buttonBorder}
                                        >
                                            <NewReleasesOutlinedIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            )}
                            {props.user && (
                                <Grid item>
                                    <ChangelogDialog open={openDialog} />
                                </Grid>
                            )}
                        </Grid>
                    </ListItem>

                    <LoggedAs user={user} />

                    {user && user.State === USER_STATE.ACTIVE && (
                        <MenuLink
                            to="/"
                            name="Home"
                            handler={handleDrawerToggle}
                            icon={<HomeIcon />}
                        />
                    )}
                    {assertUserPermissions("/users", user?.Permissions) && (
                        <MenuLink
                            to="/users"
                            name="Users"
                            handler={handleDrawerToggle}
                            icon={<PersonAddIcon />}
                        />
                    )}
                    {assertUserPermissions("/groups", user?.Permissions) && (
                        <MenuLink
                            to="/groups"
                            name="Groups"
                            handler={handleDrawerToggle}
                            icon={<PersonAddIcon />}
                        />
                    )}
                    {assertUserPermissions("/access-requests", user?.Permissions) && (
                        <MenuLink
                            to="/access-requests"
                            name="Access requests"
                            handler={handleDrawerToggle}
                            icon={
                                <StyledBadge
                                    badgeContent={accessRequests}
                                    max={9}
                                    color="error"
                                    overlap="rectangular"
                                >
                                    <AssignmentIndIcon />
                                </StyledBadge>
                            }
                        />
                    )}
                    {assertUserPermissions("/overtime", user?.Permissions) && (
                        <MenuLink
                            disabled
                            to="/overtime"
                            name="Overtime requests"
                            handler={handleDrawerToggle}
                            icon={
                                <StyledBadge
                                    invisible
                                    badgeContent={overtimes}
                                    max={9}
                                    color="error"
                                    overlap="rectangular"
                                >
                                    <AssignmentIndIcon />
                                </StyledBadge>
                            }
                        />
                    )}
                    {assertUserPermissions("/projects", user?.Permissions) && (
                        <MenuLink
                            to="/projects"
                            name="Projects"
                            handler={handleDrawerToggle}
                            icon={<AssignmentIcon />}
                        />
                    )}
                    {assertUserPermissions("/billable-projects", user?.Permissions) && (
                        <MenuLink
                            to="/billable-projects"
                            name="Billable projects"
                            handler={handleDrawerToggle}
                            icon={<AssignmentIcon />}
                        />
                    )}
                    {assertUserPermissions("/reports/advanced", user?.Permissions) && (
                        <MenuLink
                            to="/reports/advanced"
                            name="Advanced report"
                            handler={handleDrawerToggle}
                            icon={<TableChartIcon />}
                        />
                    )}
                    {assertUserPermissions("/reports/presence", user?.Permissions) && (
                        <MenuLink
                            to="/reports/presence"
                            name="Presence report"
                            handler={handleDrawerToggle}
                            icon={<TableChartIcon />}
                        />
                    )}
                    {assertUserPermissions("/reports/projects", user?.Permissions) && (
                        <MenuLink
                            to="/reports/projects"
                            name="Project report"
                            handler={handleDrawerToggle}
                            icon={<TableChartIcon />}
                        />
                    )}
                    {assertUserPermissions("/reports/project-plus", user?.Permissions) && (
                        <MenuLink
                            to="/reports/project-plus"
                            name="Project report plus"
                            handler={handleDrawerToggle}
                            icon={<TableChartIcon />}
                        />
                    )}
                    {assertUserPermissions("/reports/users", user?.Permissions) && (
                        <MenuLink
                            to="/reports/users"
                            name="Users report"
                            handler={handleDrawerToggle}
                            icon={<TableChartIcon />}
                        />
                    )}
                    {assertUserPermissions("/reports/customer", user?.Permissions) && (
                        <MenuLink
                            to="/reports/customer"
                            name="Customer report"
                            icon={<TableChartIcon />}
                        />
                    )}
                    {assertUserPermissions("/exports/enova", user?.Permissions) && (
                        <MenuLink
                            to="/exports/enova"
                            name="Export to enova"
                            icon={<PublishIcon />}
                        />
                    )}
                    {assertUserPermissions("/lock", user?.Permissions) && (
                        <MenuLink
                            to="/lock"
                            name="Lock worklogs"
                            handler={handleDrawerToggle}
                            icon={<LockIcon />}
                        />
                    )}
                    {assertUserPermissions("MailModule", user?.Permissions) && (
                        <MenuLink
                            to="/mail"
                            name="Notification manager"
                            handler={handleDrawerToggle}
                            icon={<NotificationsPausedIcon />}
                        />
                    )}
                    {assertUserPermissions("/locations", user?.Permissions) && (
                        <MenuLink
                            to="/locations"
                            name="Locations"
                            handler={handleDrawerToggle}
                            icon={<RoomIcon />}
                        />
                    )}
                    {assertUserPermissions("/templatesManager", user?.Permissions) && (
                        <MenuLink
                            to="/templatesManager"
                            name="Templates Manager"
                            handler={handleDrawerToggle}
                            icon={<EventIcon />}
                        />
                    )}
                    {user && (
                        <MenuLink
                            to="/help"
                            name="FAQ"
                            handler={handleDrawerToggle}
                            icon={<LiveHelpIcon />}
                        />
                    )}

                    {user ? (
                        <MenuButton
                            name="Logout"
                            icon={<PowerSettingsNewIcon />}
                            handler={onLogout}
                        />
                    ) : (
                        <MenuButton name="Login" icon={<ExitToAppIcon />} handler={onLogin} />
                    )}

                    <ListItem className={classes.versionItem}>
                        <Version />
                    </ListItem>
                </List>
            </Drawer>
        </nav>
    );
}

export default SideMenu;
